/**
 * Task #1829 — Modale dinamico data-driven per connessione MCP preset.
 *
 * Renderizza i custom_fields dichiarati dal preset (text/select/multi-select)
 * + nome connessione (multi-account) + checkbox scope opzionali, poi POST a
 * `/mcp/catalog/connect/:slug` → ottiene `authorization_url` → redirect vendor.
 */

import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Loader2, ExternalLink } from 'lucide-react';
import { apiClient as api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/utils';

/**
 * Logo del preset servito dall'origin dell'app (`/mcp-logos/<slug>.svg`):
 * evita la dipendenza dal CDN esterno con cert non valido. Fallback grazioso a
 * letter-avatar in accent color se l'asset manca (preset senza logo bundlato).
 */
export function PresetLogo({ slug, name, accentColor }: { slug: string; name: string; accentColor: string }): JSX.Element {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <div
        className="h-10 w-10 rounded shrink-0 flex items-center justify-center text-white font-semibold text-sm"
        style={{ backgroundColor: accentColor }}
        aria-label={name}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={`/mcp-logos/${slug}.svg`}
      alt={name}
      className="h-10 w-10 rounded shrink-0 object-contain"
      onError={() => setErrored(true)}
    />
  );
}

export interface KnownTool {
  name: string;
  label: string;
  description?: string;
}

export interface CatalogPreset {
  slug: string;
  name: string;
  description_it: string;
  description_en: string | null;
  logo_url: string;
  accent_color: string;
  category_label: string;
  doc_url: string | null;
  badge: string | null;
  scopes_default: string[];
  scopes_optional: string[];
  custom_fields_schema: CustomFieldSpec[];
  known_tools: KnownTool[];
  configured: boolean;
  requires_credentials: boolean;
  credential_mode: 'dcr' | 'platform' | 'tenant_byo';
  has_tenant_credentials: boolean;
}

interface CustomFieldSpec {
  name: string;
  label: string;
  type: 'text' | 'select' | 'multi-select';
  required: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  help?: string;
}

interface Props {
  preset: CatalogPreset | null;
  open: boolean;
  onClose: () => void;
}

export default function PresetConnectModal({ preset, open, onClose }: Props) {
  const { toast } = useToast();
  const [connectionName, setConnectionName] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, string | string[]>>({});
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preset) {
      setConnectionName(preset.name);
      const initial: Record<string, string | string[]> = {};
      for (const f of preset.custom_fields_schema ?? []) {
        initial[f.name] = f.type === 'multi-select' ? [] : '';
      }
      setCustomFields(initial);
      setSelectedScopes([...(preset.scopes_default ?? [])]);
      setSelectedTools((preset.known_tools ?? []).map((t) => t.name));
      setClientId('');
      setClientSecret('');
    }
  }, [preset]);

  const optionalScopes = useMemo(() => preset?.scopes_optional ?? [], [preset]);
  const knownTools = useMemo(() => preset?.known_tools ?? [], [preset]);

  if (!preset) return null;

  // Modello ibrido: i preset BYO sono sempre connettibili (le credenziali le porta il tenant).
  const isByo = preset.credential_mode === 'tenant_byo';
  const credentialsRequired = isByo && !preset.has_tenant_credentials;
  const connectable = preset.configured || isByo;

  const toggleScope = (scope: string): void => {
    setSelectedScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  const toggleTool = (toolName: string): void => {
    setSelectedTools((prev) => (prev.includes(toolName) ? prev.filter((t) => t !== toolName) : [...prev, toolName]));
  };

  const toggleMultiSelect = (fieldName: string, value: string): void => {
    setCustomFields((prev) => {
      const current = (prev[fieldName] as string[]) ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [fieldName]: next };
    });
  };

  const handleSubmit = async (): Promise<void> => {
    if (!connectable) {
      toast({ title: 'Configurazione richiesta', description: 'Credenziali OAuth non ancora configurate per questo preset.', variant: 'destructive' });
      return;
    }
    if (!connectionName.trim()) {
      toast({ title: 'Nome connessione richiesto', variant: 'destructive' });
      return;
    }
    if (credentialsRequired && !clientId.trim()) {
      toast({ title: 'Client ID richiesto', description: 'Inserisci le credenziali della tua app vendor.', variant: 'destructive' });
      return;
    }
    for (const f of preset.custom_fields_schema ?? []) {
      const v = customFields[f.name];
      if (f.required && (!v || (Array.isArray(v) && v.length === 0))) {
        toast({ title: `Campo "${f.label}" richiesto`, variant: 'destructive' });
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ authorization_url?: string }>(
        `/mcp/catalog/connect/${preset.slug}`,
        {
          connection_name: connectionName.trim(),
          custom_fields: customFields,
          scopes: selectedScopes,
          selected_tools: selectedTools,
          ...(isByo && clientId.trim() ? { client_id: clientId.trim() } : {}),
          ...(isByo && clientSecret.trim() ? { client_secret: clientSecret.trim() } : {}),
        },
      );
      const url = res.data?.authorization_url;
      if (!url) {
        toast({ title: 'Errore', description: 'URL autorizzazione mancante', variant: 'destructive' });
        return;
      }
      window.location.href = url;
    } catch (e) {
      toast({ title: 'Connessione fallita', description: getErrorMessage(e), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <PresetLogo slug={preset.slug} name={preset.name} accentColor={preset.accent_color} />
            <div>
              <DialogTitle>{preset.name}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" style={{ borderColor: preset.accent_color, color: preset.accent_color }}>
                  {preset.category_label}
                </Badge>
                {preset.badge && <Badge variant="secondary">{preset.badge}</Badge>}
              </div>
            </div>
          </div>
          <DialogDescription className="pt-2">{preset.description_it}</DialogDescription>
          {preset.doc_url && (
            <a href={preset.doc_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1">
              Documentazione ufficiale <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!connectable && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <span className="font-medium">Configurazione richiesta.</span> Le credenziali OAuth per questo preset non
              sono ancora configurate. Contatta l'amministratore della piattaforma per abilitarlo.
            </div>
          )}

          {isByo && (
            <div className="space-y-3 rounded-md border border-border bg-muted/40 px-3 py-3">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Credenziali della tua app {preset.name}.</span>{' '}
                Crea un'app OAuth sul portale {preset.name} e incolla Client ID e Secret.
                {preset.has_tenant_credentials && ' Sono già salvate: lascia vuoto per riusarle.'}
                {preset.doc_url && (
                  <a href={preset.doc_url} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 ml-1">
                    Guida <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="byo-client-id">
                  Client ID{credentialsRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="byo-client-id"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder={preset.has_tenant_credentials ? '•••••• (salvato)' : 'Client ID app vendor'}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="byo-client-secret">Client Secret</Label>
                <Input
                  id="byo-client-secret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={preset.has_tenant_credentials ? '•••••• (salvato)' : 'Client Secret app vendor'}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="connection-name">Nome connessione</Label>
            <Input
              id="connection-name"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="es. GitHub Work"
            />
            <p className="text-xs text-muted-foreground">Univoco per tenant — usalo per gestire più account dello stesso vendor.</p>
          </div>

          {(preset.custom_fields_schema ?? []).map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label htmlFor={`f-${f.name}`}>
                {f.label}{f.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {f.type === 'text' && (
                <Input
                  id={`f-${f.name}`}
                  value={(customFields[f.name] as string) ?? ''}
                  onChange={(e) => setCustomFields((p) => ({ ...p, [f.name]: e.target.value }))}
                  placeholder={f.placeholder}
                />
              )}
              {f.type === 'select' && (
                <select
                  id={`f-${f.name}`}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  value={(customFields[f.name] as string) ?? ''}
                  onChange={(e) => setCustomFields((p) => ({ ...p, [f.name]: e.target.value }))}
                >
                  <option value="">Seleziona…</option>
                  {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              {f.type === 'multi-select' && (
                <div className="space-y-1 max-h-40 overflow-auto border rounded-md p-2">
                  {(f.options ?? []).map((o) => {
                    const checked = ((customFields[f.name] as string[]) ?? []).includes(o.value);
                    return (
                      <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={checked} onCheckedChange={() => toggleMultiSelect(f.name, o.value)} />
                        <span>{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
            </div>
          ))}

          {knownTools.length > 0 && (
            <div className="space-y-1.5">
              <Label>Tool da abilitare</Label>
              <div className="space-y-1 max-h-40 overflow-auto border rounded-md p-2">
                {knownTools.map((t) => (
                  <label key={t.name} className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox checked={selectedTools.includes(t.name)} onCheckedChange={() => toggleTool(t.name)} className="mt-0.5" />
                    <span>
                      <span className="font-medium">{t.label}</span>
                      {t.description && <span className="block text-xs text-muted-foreground">{t.description}</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Solo i tool selezionati verranno abilitati dopo la connessione.</p>
            </div>
          )}

          {optionalScopes.length > 0 && (
            <div className="space-y-1.5">
              <Label>Scope opzionali</Label>
              <div className="space-y-1 max-h-32 overflow-auto border rounded-md p-2">
                {optionalScopes.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={selectedScopes.includes(s)} onCheckedChange={() => toggleScope(s)} />
                    <code className="text-xs">{s}</code>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting || !connectable}>
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reindirizzo…</> : 'Autorizza e connetti'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
